import type { CurrentPaymentMethod } from '@hotjar/webapp/billing/plan';
import { SubscriptionChangePriceSummary } from '@hotjar/webapp/billing/subscription';

export enum YearlyUpsellModalState {
  loading = 'loading',
  idle = 'idle',
  submitting = 'submitting',
  error = 'error',
}

export const initialState: State = {
  cardType: '',
  cardTypeAccepted: true,
  verificationFields: {
    givenName: '',
    postalCode: '',
    locality: '',
    countryCodeAlpha2: '',
  },
  fieldErrors: {
    givenName: false,
    number: false,
    expirationYear: false,
    expirationMonth: false,
    countryCodeAlpha2: false,
    countryCode: false,
    locality: false,
    postalCode: false,
    cvv: false,
  },
  errorMessage: '',
  paymentType: 'credit_card',
  isPaypalLoggedIn: false,
  paypalToken: {
    nonce: null,
    details: {
      email: '',
    },
  },
  loadingState: YearlyUpsellModalState.loading,
  expanded: true,
  paymentMethod: null,
  priceSummary: new SubscriptionChangePriceSummary(),
  hostedFields: null,
};

export interface State {
  cardType: string;
  cardTypeAccepted: boolean;
  verificationFields: {
    givenName: string;
    postalCode: string;
    locality: string;
    countryCodeAlpha2: string;
  };
  fieldErrors: {
    givenName: boolean;
    number: boolean;
    expirationYear: boolean;
    expirationMonth: boolean;
    countryCodeAlpha2: boolean;
    countryCode: boolean;
    locality: boolean;
    postalCode: boolean;
    cvv: boolean;
  };
  errorMessage: string;
  paymentType: IPaymentType;
  isPaypalLoggedIn: boolean;
  paypalToken: {
    nonce: string | null;
    details: {
      email: string;
    };
  };
  loadingState: YearlyUpsellModalState;
  expanded: boolean;
  paymentMethod?: CurrentPaymentMethod | null;
  priceSummary: SubscriptionChangePriceSummary;
  hostedFields: unknown | null;
}

export type Action =
  | { type: 'PAYMENT_TYPE_CHANGED'; payload: { paymentType: State['paymentType'] } }
  | {
      type: 'VERIFICATION_FIELDS_CHANGED';
      payload: { verificationFields: State['verificationFields'] };
    }
  | { type: 'SET_CARD_TYPE'; payload: { accepted: boolean; type: string } }
  | { type: 'SET_FIELD_ERRORS'; payload: { fieldErrors: Partial<State['fieldErrors']> } }
  | { type: 'ON_VALIDATION_SUCCESS' }
  | { type: 'SET_PAYPAL_LOGIN_DETAILS'; payload: { token: State['paypalToken'] } }
  | { type: 'ON_PAYPAL_LOGOUT' }
  | { type: 'ON_ERROR'; payload: { error: string } }
  | { type: 'ON_SUBMIT' }
  | { type: 'ON_SUBMIT_COMPLETE' }
  | { type: 'ON_EXPAND_TOGGLE' }
  | {
      type: 'ON_LOAD_SUCCESS';
      payload: {
        paymentMethod?: CurrentPaymentMethod;
        hostedField: unknown;
        priceSummary: SubscriptionChangePriceSummary;
      };
    }
  | { type: 'ON_LOAD_FAILURE'; payload: { errorMessage: string } };

export const ACTIONS = {
  PAYMENT_TYPE_CHANGED: 'PAYMENT_TYPE_CHANGED',
  VERIFICATION_FIELDS_CHANGED: 'VERIFICATION_FIELDS_CHANGED',
  SET_CARD_TYPE: 'SET_CARD_TYPE',
  SET_FIELD_ERRORS: 'SET_FIELD_ERRORS',
  ON_VALIDATION_SUCCESS: 'ON_VALIDATION_SUCCESS',
  SET_PAYPAL_LOGIN_DETAILS: 'SET_PAYPAL_LOGIN_DETAILS',
  ON_PAYPAL_LOGOUT: 'ON_PAYPAL_LOGOUT',
  ON_ERROR: 'ON_ERROR',
  SET_COUNTRIES: 'SET_COUNTRIES',
  ON_SUBMIT: 'ON_SUBMIT',
  ON_SUBMIT_COMPLETE: 'ON_SUBMIT_COMPLETE',
  ON_EXPAND_TOGGLE: 'ON_EXPAND_TOGGLE',
  ON_LOAD_SUCCESS: 'ON_LOAD_SUCCESS',
  ON_LOAD_FAILURE: 'ON_LOAD_FAILURE',
} as const;

export const Reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ACTIONS.PAYMENT_TYPE_CHANGED:
      return { ...state, ...action.payload };

    case ACTIONS.VERIFICATION_FIELDS_CHANGED:
      return { ...state, ...action.payload };

    case ACTIONS.SET_CARD_TYPE:
      return { ...state, cardType: action.payload.type, cardTypeAccepted: action.payload.accepted };

    case ACTIONS.SET_FIELD_ERRORS:
      return {
        ...state,
        fieldErrors: { ...state.fieldErrors, ...action.payload.fieldErrors },
      };

    case ACTIONS.ON_VALIDATION_SUCCESS:
      return {
        ...state,
        fieldErrors: {
          givenName: false,
          number: false,
          expirationYear: false,
          expirationMonth: false,
          countryCodeAlpha2: false,
          countryCode: false,
          locality: false,
          postalCode: false,
          cvv: false,
        },
      };

    case ACTIONS.SET_PAYPAL_LOGIN_DETAILS:
      return { ...state, isPaypalLoggedIn: true, paypalToken: action.payload.token };

    case ACTIONS.ON_PAYPAL_LOGOUT:
      return {
        ...state,
        isPaypalLoggedIn: false,
        paypalToken: {
          nonce: 'null',
          details: {
            email: '',
          },
        },
      };

    case ACTIONS.ON_ERROR:
      return {
        ...state,
        // When we have a card error we clear the paypal state
        isPaypalLoggedIn: false,
        paypalToken: {
          nonce: null,
          details: {
            email: '',
          },
        },
        // Set the form error
        errorMessage: action.payload.error,
      };

    case ACTIONS.ON_SUBMIT:
      return { ...state, loadingState: YearlyUpsellModalState.submitting };

    case ACTIONS.ON_SUBMIT_COMPLETE:
      return { ...state, loadingState: YearlyUpsellModalState.idle };

    case ACTIONS.ON_EXPAND_TOGGLE:
      return { ...state, expanded: !state.expanded };

    case ACTIONS.ON_LOAD_SUCCESS:
      return {
        ...state,
        expanded: !action.payload.paymentMethod?.available,
        ...action.payload,
        loadingState: YearlyUpsellModalState.idle,
      };

    case ACTIONS.ON_LOAD_FAILURE:
      return {
        ...state,
        expanded: false,
        hostedFields: null,
        paymentMethod: null,
        priceSummary: new SubscriptionChangePriceSummary(),
        loadingState: YearlyUpsellModalState.error,
        errorMessage: action.payload.errorMessage,
      };
    default:
      return state;
  }
};
